/**
 * Narrowing the roster, in one place, for every view that shows it.
 *
 * The guest table had no filter at all: 137 rows, sorted, and a host looking
 * for one person scrolled. The seating picker had a sort standing in for a
 * filter, which put the 40 who are coming above the 97 who never replied and
 * left nothing on screen saying where one group ended. Both are the same
 * question — show me a subset of these people — so the answer lives here rather
 * than twice, differently.
 *
 * Every predicate is additive: a filter with nothing set matches everyone, and
 * each field set narrows further. That is what makes "coming, staying at the
 * Bairro, name contains 'ma'" work without a combinatorial mess of modes.
 */
import type { TrackerGuest } from '../services/tracker-api-client';

/** What a guest's attendance resolves to for filtering purposes. */
export type GuestStatus = 'going' | 'maybe' | 'declined' | 'no_reply';

export interface GuestFilter {
  /** Matched against name, email and phone. */
  query: string;
  /** Empty means every status. */
  statuses: GuestStatus[];
  /** Empty means every hotel; `'none'` is "no hotel on file". */
  stayingAt: string[];
  /** `YYYY-MM-DD`; keeps only guests in town that day. */
  hereOn: string;
  /**
   * Tag ids, ANDed. "College friends" ∧ "going" is the useful question — a host
   * narrowing to a cohort wants the intersection, and OR across two tags is
   * indistinguishable from no tag filter once three are ticked.
   */
  tagIds: string[];
  /**
   * "Who have I not tagged yet." How a host finishes a tagging pass, and the
   * one query no combination of tag chips can express. Combined with `tagIds`
   * it is a contradiction, so it wins and the chips are cleared by the bar.
   */
  withoutTags: boolean;
}

export const EMPTY_GUEST_FILTER: GuestFilter = {
  query: '',
  statuses: [],
  stayingAt: [],
  hereOn: '',
  tagIds: [],
  withoutTags: false,
};

export const GUEST_STATUS_LABELS: Record<GuestStatus, string> = {
  going: 'Going',
  maybe: 'Maybe',
  declined: "Can't go",
  no_reply: 'No reply',
};

/**
 * Reads `going` first, which is the resolved answer the Guests tab displays.
 * `rsvpStatus` is only what the guest last said themself, and a host who has
 * marked somebody as coming expects the filter to agree with the column.
 */
export function guestStatus(guest: Pick<TrackerGuest, 'going' | 'rsvpStatus'>): GuestStatus {
  const resolved = String(guest.going ?? '').trim().toLowerCase()
    || String(guest.rsvpStatus ?? '').trim().toLowerCase();
  if (resolved === 'going' || resolved === 'yes' || resolved === 'true') return 'going';
  if (resolved === 'maybe') return 'maybe';
  if (resolved === 'declined' || resolved === 'not_going' || resolved === 'no') return 'declined';
  return 'no_reply';
}

/** `YYYY-MM-DD` off a date field, ignoring any time and zone that came with it. */
function dayOf(raw: unknown): string {
  const value = String(raw ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
}

/**
 * In town on `day`. Compared as strings, which sort correctly and carry no
 * timezone — parsing these into Dates is how "arrives the 15th" becomes the
 * 14th for a host whose browser sits west of the venue. A missing date is not
 * evidence of absence, so a guest with neither is kept.
 */
export function isHereOn(
  guest: Pick<TrackerGuest, 'arrivalDate' | 'departureDate'>,
  day: string,
): boolean {
  if (!day) return true;
  const arrival = dayOf(guest.arrivalDate);
  const departure = dayOf(guest.departureDate);
  if (arrival && day < arrival) return false;
  if (departure && day > departure) return false;
  return true;
}

function matchesQuery(guest: TrackerGuest, needle: string): boolean {
  if (!needle) return true;
  return [guest.fullName, guest.email, guest.phone, guest.stayingAt]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

export function filterGuests(
  guests: ReadonlyArray<TrackerGuest> | null | undefined,
  filter: Partial<GuestFilter> | null | undefined,
): TrackerGuest[] {
  const active = { ...EMPTY_GUEST_FILTER, ...(filter ?? {}) };
  const needle = active.query.trim().toLowerCase();

  return (guests ?? []).filter((guest) => {
    if (!matchesQuery(guest, needle)) return false;
    if (active.statuses.length && !active.statuses.includes(guestStatus(guest))) return false;
    if (active.stayingAt.length) {
      const hotel = String(guest.stayingAt ?? '').trim();
      if (!active.stayingAt.includes(hotel || 'none')) return false;
    }
    if (!isHereOn(guest, active.hereOn)) return false;
    /*
      Untagged wins over the tag chips: the two together mean "carries these
      tags and carries none", which is nobody. A filter that silently answers
      "0 guests" is worse than one that refuses, so the bar clears the chips
      when this goes on — this clause is only the backstop for a caller that
      sets both.
    */
    if (active.withoutTags) {
      if ((guest.tagIds ?? []).length > 0) return false;
    } else if (active.tagIds.length) {
      const held = new Set(guest.tagIds ?? []);
      // AND, not OR: every ticked tag has to be on the guest.
      if (!active.tagIds.every((id) => held.has(id))) return false;
    }
    return true;
  });
}

/** True when the filter would narrow anything — drives the "clear" affordance. */
export function isFiltering(filter: Partial<GuestFilter> | null | undefined): boolean {
  const active = { ...EMPTY_GUEST_FILTER, ...(filter ?? {}) };
  return Boolean(
    active.query.trim() ||
      active.statuses.length ||
      active.stayingAt.length ||
      active.hereOn ||
      active.tagIds.length ||
      active.withoutTags,
  );
}

/** "12 of 137 guests" — always both numbers, so a filter can never look like the whole roster. */
export function filterSummary(shown: number, total: number): string {
  if (shown === total) return `${total} ${total === 1 ? 'guest' : 'guests'}`;
  return `${shown} of ${total} ${total === 1 ? 'guest' : 'guests'}`;
}
